import { useState, useMemo } from "react";
import {
  pendingRequests,
  REQUEST_STATUS,
  canApprove,
} from "../../utils/openHouseOpportunityHelpers";
import { cancelOpportunity } from "../../utils/firebasefunctions";
import RequestToHostModal from "./RequestToHostModal";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";

export default function UpForGrabsBoard({ opportunities, onPost }) {
  const [{ user }] = useStateValue();
  const [requestTarget, setRequestTarget] = useState(null);

  // Only Open opportunities are claimable.
  const openOps = useMemo(
    () => (opportunities || []).filter((o) => o.status === "Open"),
    [opportunities],
  );

  const hasMyPending = (o) =>
    (o.requests || []).some(
      (r) => r.agentId === user?.id && r.status === REQUEST_STATUS.PENDING,
    );

  // The poster, the routed LC, or any admin/supervisor can cancel a slot.
  const canManage = (o) => o.postedBy === user?.id || canApprove(o, user);
  const handleCancel = async (o) => {
    if (!window.confirm("Cancel this up-for-grabs slot?")) return;
    try {
      await cancelOpportunity(o.id, user.id);
      toast.success("Slot cancelled.");
    } catch (err) {
      console.error("Cancel opportunity failed:", err);
      toast.error("Could not cancel. Try again.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">
          {openOps.length} open {openOps.length === 1 ? "slot" : "slots"} you can host
        </p>
        <button
          onClick={onPost}
          className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-3 py-2 rounded-md"
        >
          Post Up For Grabs
        </button>
      </div>

      {openOps.length === 0 ? (
        <div className="text-sm text-slate-400 py-10 text-center">
          No open houses are up for grabs right now.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {openOps.map((o) => {
            const count = pendingRequests(o.requests).length;
            const mine = hasMyPending(o);
            return (
              <div key={o.id} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
                <div className="p-4">
                  <div className="font-semibold text-slate-900">
                    {o.propertyAddress?.split(",")[0] || "Listing"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Listing: {o.listingAgentName || "—"}
                    {o.suggestedTiming ? ` · ${o.suggestedTiming}` : " · Any time"}
                  </div>
                  <div className="text-xs font-semibold text-blue-700 mt-2">
                    {count === 0
                      ? "No requests yet"
                      : `${count} ${count === 1 ? "agent has" : "agents have"} requested`}
                  </div>
                  <button
                    onClick={() => setRequestTarget(o)}
                    disabled={mine}
                    className="mt-3 w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white text-sm font-semibold px-3 py-2 rounded-md"
                  >
                    {mine ? "Requested ✓" : "Request to Host"}
                  </button>
                  {canManage(o) && (
                    <button
                      onClick={() => handleCancel(o)}
                      className="mt-2 w-full text-xs font-semibold text-red-600 hover:text-red-700"
                    >
                      Cancel Slot
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RequestToHostModal
        open={!!requestTarget}
        opportunity={requestTarget}
        onClose={() => setRequestTarget(null)}
      />
    </div>
  );
}
